import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Table,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2FormField,
  Alert,
  LoadingState,
  StatePanel,
  type V2TableColumn,
  type V2BadgeTone,
  V2FormGrid,
  V2FormActions,
} from '@sakhtar/design-system/react';
import { addDays, systemClock } from '@sakhtar/calendar';
import { HubKpi, HubKpiGrid } from '../../components/hub/HubKpi.js';
import { useApiGet, type ApiGetState } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { QuickTaskForm } from './QuickTaskForm.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  NO_PROJECT_LABEL,
  type MyWorkResponse,
  type OverdueTask,
  type ProjectListResponse,
  type ProjectsOverdueReportResponse,
  type ProjectsStatusReportResponse,
} from '../../lib/types/projects.js';
import './projects-hub.css';
import { HubYarCard } from '../../shell/yar/HubYarCard.js';

const STATUS_TONE: Record<string, V2BadgeTone> = {
  برنامه‌ریزی: 'neutral',
  فعال: 'success',
  متوقف: 'warn',
  تکمیل‌شده: 'info',
  'لغو شده': 'danger',
};

/** Board window for "نزدیک به سررسید" (days ahead of today). */
const NEAR_DUE_DAYS = 7;
/** Rows shown in each card; the full lists live on the module tabs. */
const PROJECT_ROWS = 6;
const OVERDUE_TASK_ROWS = 5;

const fa = (n: number) => n.toLocaleString('fa-IR');

interface ProjectRow {
  readonly id: string;
  readonly ref: string;
  readonly name: string;
  readonly statusLabel: string;
  readonly dueDate: string | null;
  /** Set only for projects that appear in the real overdue report. */
  readonly daysOverdue: number | null;
  readonly nearDue: boolean;
}

/** In-place StatePanel for one failed section (403 → permission, else network). */
function SectionError({ res }: { readonly res: ApiGetState<unknown> }) {
  if (res.status !== 'error') return null;
  const denied = res.error.status === 403;
  return (
    <StatePanel
      variant={denied ? 'permission_denied' : 'network_error'}
      title={denied ? 'دسترسی ندارید' : 'اطلاعات قابل نمایش نیست'}
    />
  );
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — frame `/t/kimia/projects` (section `isProj`)
 * canonical pattern: module OVERVIEW — 3-tile KPI row (hero + two tinted tiles),
 *   then a wide «پروژه‌ها» table card beside a «کارهای عقب‌افتاده» list card.
 * desktop reference: same frame, two-column row (1.7 : 1, wraps under ~900px).
 * mobile reference: cards stack single-column at 390px (flex-wrap, min-width: 0).
 * light/dark reference: tokens only (`[data-module="projects"]` tint); no hex colors.
 * state reference: loading · permission_denied (per section) · network_error
 *   (per section) · empty (per card).
 * v2 status: aligned — with the documented deviations below.
 *
 * Module chrome: `ProjectsModuleLayout` still renders the v1 PageHeader + tabs
 * (it already sets `data-module="projects"`); the page body below is v2.
 *
 * Every number/row is real; nothing is invented:
 *   - «پروژهٔ فعال» (hero): GET .../projects/reports/status → projects.rows[status=active].
 *     Foot «N مورد نزدیک به سررسید» + ticks: active projects (GET .../projects?status=active
 *     &limit=200) whose dueDate falls in [today, today+7] (`addDays` from @sakhtar/calendar,
 *     compared as ISO strings). Omitted when the envelope `total` exceeds the rows fetched
 *     (an incomplete page must not pose as a count).
 *   - «کار باز»: reports/status → tasks.rows[todo + in_progress]. Foot «N کار عقب‌افتاده»
 *     + danger bar (overdue ÷ open, capped at 100%) from GET .../projects/reports/overdue.
 *   - «کار امروز»: GET .../projects/my-work → tasks due today. Foot says «در کارتابل من»
 *     (not the reference's «اختصاص به شما»): my-work returns assigned OR owned tasks.
 *   - «پروژه‌ها» table: overdue projects first (reports/overdue, with real daysOverdue),
 *     then active projects by dueDate. Badge: overdue → «N روز تأخیر» (danger), due within
 *     7 days → «نزدیک به سررسید» (warn), otherwise the real status label.
 *   - «کارهای عقب‌افتاده»: reports/overdue → tasks, worst first, with the real daysOverdue.
 *
 * Deviations (KIT_CONFORMANT — the design shows data no endpoint supplies):
 *   - Table columns «مسئول», «کار باز» (per project) and «پیشرفت»: the project list DTO is
 *     { ref, name, statusLabel, dueDate } only (apps/api/src/projects/presentation.ts
 *     `toProjectListItem`); owner is not serialized, and per-project task counts/progress
 *     would need one GET .../projects/:ref per row. The «وضعیت» badge shows real status /
 *     lateness instead of the design's «در مسیر / در خطر تأخیر» health, which has no source.
 *   - Overdue-task rows have no checkbox (no inline complete action on this overview) and no
 *     assignee line (OverdueTask carries projectRef only).
 *   - Removed vs. the previous build because the v2 frame has no equivalent: the two status
 *     donuts (+ their /projects/list?status= drill chips), «خلاصهٔ کارهای معوق» buckets,
 *     «کارتابل من», «سررسیدهای نزدیک», «پروژه‌های فعال اخیر» (with its unvalidated
 *     any-status Select — status transitions live on ProjectListPage), and the 4th KPI
 *     (overdue count now lives in the «کار باز» foot and the overdue card).
 *   - The primary «پروژهٔ جدید» CTA stays in this page's action row, because the module
 *     header (ProjectsModuleLayout, v1) is outside this file.
 */
export function ProjectsHubPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);

  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDueDate, setProjectDueDate] = useState('');
  const [projectError, setProjectError] = useState<string | null>(null);
  const [creatingProject, setCreatingProject] = useState(false);

  const [showTaskForm, setShowTaskForm] = useState(false);

  const projects = useApiGet<ProjectListResponse>(
    slug ? `${base}/api/v1/projects?status=active&limit=200` : null,
    [slug, refreshKey],
  );
  const statusReport = useApiGet<ProjectsStatusReportResponse>(
    slug ? `${base}/api/v1/projects/reports/status` : null,
    [slug, refreshKey],
  );
  const overdueReport = useApiGet<ProjectsOverdueReportResponse>(
    slug ? `${base}/api/v1/projects/reports/overdue` : null,
    [slug, refreshKey],
  );
  const myWork = useApiGet<MyWorkResponse>(slug ? `${base}/api/v1/projects/my-work` : null, [
    slug,
    refreshKey,
  ]);

  const today = systemClock.today();
  const nearDueHorizon = addDays(today, NEAR_DUE_DAYS);

  const activeProjectsCount = useMemo(() => {
    if (statusReport.status !== 'ready') return null;
    return statusReport.data.projects.rows.find((r) => r.status === 'active')?.count ?? 0;
  }, [statusReport]);

  const openTasksCount = useMemo(() => {
    if (statusReport.status !== 'ready') return null;
    return statusReport.data.tasks.rows
      .filter((r) => r.status === 'todo' || r.status === 'in_progress')
      .reduce((sum, r) => sum + r.count, 0);
  }, [statusReport]);

  const overdueTasksCount =
    overdueReport.status === 'ready' ? overdueReport.data.tasks.length : null;

  const myTasksTodayCount =
    myWork.status === 'ready' ? myWork.data.tasks.filter((t) => t.dueDate === today).length : null;

  const isNearDue = (dueDate: string | null) => {
    if (!dueDate) return false;
    const day = dueDate.slice(0, 10);
    return day >= today && day <= nearDueHorizon;
  };

  /** Near-due count — only when the fetched page is the whole active set. */
  const nearDueCount = useMemo(() => {
    if (projects.status !== 'ready') return null;
    const { projects: rows, total } = projects.data;
    if (total !== undefined && total > rows.length) return null;
    return rows.filter((p) => isNearDue(p.dueDate)).length;
  }, [projects, today, nearDueHorizon]);

  const projectRows = useMemo<readonly ProjectRow[]>(() => {
    if (projects.status !== 'ready') return [];
    const byRef = new Map<string, ProjectRow>();
    if (overdueReport.status === 'ready') {
      for (const p of overdueReport.data.projects) {
        byRef.set(p.ref, {
          id: p.ref,
          ref: p.ref,
          name: p.name,
          statusLabel: p.statusLabel,
          dueDate: p.dueDate,
          daysOverdue: p.daysOverdue,
          nearDue: false,
        });
      }
    }
    for (const p of projects.data.projects) {
      if (byRef.has(p.ref)) continue;
      byRef.set(p.ref, {
        id: p.ref,
        ref: p.ref,
        name: p.name,
        statusLabel: p.statusLabel,
        dueDate: p.dueDate,
        daysOverdue: null,
        nearDue: isNearDue(p.dueDate),
      });
    }
    return [...byRef.values()]
      .sort((a, b) => {
        if (a.daysOverdue !== null || b.daysOverdue !== null) {
          return (b.daysOverdue ?? -1) - (a.daysOverdue ?? -1);
        }
        if (a.dueDate === null || b.dueDate === null) {
          return a.dueDate === b.dueDate ? 0 : a.dueDate === null ? 1 : -1;
        }
        return a.dueDate.localeCompare(b.dueDate);
      })
      .slice(0, PROJECT_ROWS);
  }, [projects, overdueReport, today, nearDueHorizon]);

  const worstOverdueTasks = useMemo<readonly OverdueTask[]>(() => {
    if (overdueReport.status !== 'ready') return [];
    return [...overdueReport.data.tasks]
      .sort((a, b) => b.daysOverdue - a.daysOverdue)
      .slice(0, OVERDUE_TASK_ROWS);
  }, [overdueReport]);

  if (!slug) return null;

  async function onCreateProject(event: FormEvent) {
    event.preventDefault();
    setProjectError(null);
    setCreatingProject(true);
    try {
      await apiPost(`${base}/api/v1/projects`, {
        name: projectName.trim(),
        dueDate: projectDueDate || undefined,
      });
      setProjectName('');
      setProjectDueDate('');
      setShowProjectForm(false);
      setRefreshKey((k) => k + 1);
    } catch {
      setProjectError('ثبت پروژه ناموفق بود.');
    } finally {
      setCreatingProject(false);
    }
  }

  const sources = [projects, statusReport, overdueReport, myWork];
  const anyLoading = sources.some((r) => r.status === 'loading');
  const allFailed = sources.every((r) => r.status === 'error');
  const allDenied = sources.every((r) => r.status === 'error' && r.error.status === 403);

  const projectColumns: readonly V2TableColumn<ProjectRow>[] = [
    {
      key: 'name',
      header: 'پروژه',
      render: (row) => (
        <Link className="prj-ov__link" to={`${base}/projects/list/${encodeURIComponent(row.ref)}`}>
          {row.name}
        </Link>
      ),
    },
    {
      key: 'due',
      header: 'سررسید',
      render: (row) => (
        <span
          className="sk-num"
          style={
            row.daysOverdue !== null
              ? { color: 'var(--prj-danger-text, var(--sk-danger))' }
              : undefined
          }
        >
          {row.dueDate ? displayJalaliDate(row.dueDate) : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) =>
        row.daysOverdue !== null ? (
          <V2Badge tone="danger">{fa(row.daysOverdue)} روز تأخیر</V2Badge>
        ) : row.nearDue ? (
          <V2Badge tone="warn">نزدیک به سررسید</V2Badge>
        ) : (
          <V2Badge tone={STATUS_TONE[row.statusLabel] ?? 'neutral'}>{row.statusLabel}</V2Badge>
        ),
    },
  ];

  const overdueShown = worstOverdueTasks.length;
  const overdueTotal = overdueTasksCount ?? 0;
  const openTotal = openTasksCount ?? 0;
  const overduePercent =
    openTotal > 0 ? Math.min(100, Math.round((overdueTotal / openTotal) * 100)) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <HubYarCard moduleId="projects" />
      <div className="prj-hub__actions">
        <V2Button variant="secondary" onClick={() => setShowTaskForm((v) => !v)}>
          کار جدید
        </V2Button>
        <V2Button variant="primary" icon="plus" onClick={() => setShowProjectForm((v) => !v)}>
          پروژه جدید
        </V2Button>
      </div>

      {showProjectForm ? (
        <V2Card>
          <V2CardHeader icon="projects" title="پروژه جدید" />
          <form onSubmit={onCreateProject} style={{ padding: '4px 20px 20px' }}>
            <V2FormGrid>
              <V2FormField label="نام" required width="wide">
                <input
                  className="sk-input"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </V2FormField>
              <V2FormField label="سررسید">
                <JalaliDateInput value={projectDueDate} onChange={setProjectDueDate} />
              </V2FormField>
            </V2FormGrid>
            <V2FormActions>
              <V2Button type="submit" variant="primary" disabled={creatingProject}>
                {creatingProject ? 'در حال ثبت…' : 'ثبت پروژه'}
              </V2Button>
            </V2FormActions>
          </form>
          {projectError ? (
            <div style={{ padding: '0 20px 16px' }}>
              <Alert tone="danger" title="خطا">
                {projectError}
              </Alert>
            </div>
          ) : null}
        </V2Card>
      ) : null}

      {showTaskForm ? (
        <V2Card>
          <V2CardHeader icon="tasks" title="کار جدید" />
          <div style={{ padding: '4px 20px 20px' }}>
            <QuickTaskForm
              slug={slug}
              showPriority
              onCreated={() => setRefreshKey((k) => k + 1)}
              onCancel={() => setShowTaskForm(false)}
            />
          </div>
        </V2Card>
      ) : null}

      {anyLoading ? <LoadingState /> : null}

      {!anyLoading && allFailed ? (
        <StatePanel
          variant={allDenied ? 'permission_denied' : 'network_error'}
          title={allDenied ? 'دسترسی ندارید' : 'اطلاعات قابل نمایش نیست'}
        />
      ) : null}

      {!anyLoading && !allFailed ? (
        <>
          <HubKpiGrid accent="projects">
            {activeProjectsCount !== null ? (
              <HubKpi
                hero
                icon="layers"
                label="پروژهٔ فعال"
                value={fa(activeProjectsCount)}
                href={`${base}/projects/list?status=active`}
                {...(nearDueCount !== null
                  ? {
                      foot:
                        nearDueCount > 0
                          ? `${fa(nearDueCount)} مورد نزدیک به سررسید`
                          : 'مورد نزدیک به سررسید ندارد',
                      ...(activeProjectsCount > 0
                        ? { ticks: { total: activeProjectsCount, hot: nearDueCount } }
                        : {}),
                    }
                  : {})}
              />
            ) : null}
            {openTasksCount !== null ? (
              <HubKpi
                label="کار باز"
                value={fa(openTasksCount)}
                href={`${base}/projects/tasks`}
                {...(overdueTasksCount !== null
                  ? {
                      foot: `${fa(overdueTasksCount)} کار عقب‌افتاده`,
                      footTone: overdueTasksCount > 0 ? ('danger' as const) : undefined,
                      ...(openTasksCount > 0
                        ? { progress: { percent: overduePercent, tone: 'danger' as const } }
                        : {}),
                    }
                  : {})}
              />
            ) : null}
            {myTasksTodayCount !== null ? (
              <HubKpi
                icon="calendar"
                label="کار امروز"
                value={fa(myTasksTodayCount)}
                href={`${base}/projects/tasks`}
                foot="در کارتابل من"
              />
            ) : null}
          </HubKpiGrid>

          <div className="prj-ov__row">
            <div className="prj-ov__main">
              <V2Card>
                <V2CardHeader
                  icon="layers"
                  title="پروژه‌ها"
                  actions={
                    <Link
                      className="sk-btn sk-btn--secondary sk-btn--sm"
                      to={`${base}/projects/list`}
                    >
                      همهٔ پروژه‌ها
                    </Link>
                  }
                />
                {projects.status === 'error' ? (
                  <SectionError res={projects} />
                ) : (
                  <V2Table
                    columns={projectColumns}
                    rows={projectRows}
                    emptyState={
                      <V2EmptyState icon="projects" compact title="پروژهٔ فعالی وجود ندارد" />
                    }
                  />
                )}
              </V2Card>
            </div>

            <div className="prj-ov__side">
              <V2Card>
                <V2CardHeader
                  icon="calendar"
                  title="کارهای عقب‌افتاده"
                  actions={
                    overdueReport.status === 'ready' ? (
                      <V2Badge tone="danger">{fa(overdueTotal)}</V2Badge>
                    ) : undefined
                  }
                />
                {overdueReport.status === 'error' ? (
                  <SectionError res={overdueReport} />
                ) : worstOverdueTasks.length === 0 ? (
                  <V2EmptyState icon="check" compact title="کار معوقی وجود ندارد" />
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {worstOverdueTasks.map((t) => (
                        <div key={t.ref} className="prj-ov__task">
                          <div className="prj-ov__task-main">
                            <span className="prj-ov__task-title">{t.title}</span>
                            <span className="prj-ov__task-sub">
                              {t.projectRef ? (
                                <Link
                                  to={`${base}/projects/list/${encodeURIComponent(t.projectRef)}`}
                                >
                                  {t.projectRef}
                                </Link>
                              ) : (
                                NO_PROJECT_LABEL
                              )}
                            </span>
                          </div>
                          <V2Badge tone="danger">{fa(t.daysOverdue)} روز تأخیر</V2Badge>
                        </div>
                      ))}
                    </div>
                    {overdueTotal > overdueShown ? (
                      <Link className="prj-ov__more" to={`${base}/projects/overdue`}>
                        مشاهدهٔ همهٔ {fa(overdueTotal)} مورد معوق
                      </Link>
                    ) : null}
                  </>
                )}
              </V2Card>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
